import { Outlet } from 'react-router-dom';
import Header from './Header';
import Sidebar from './Sidebar';
import AutoImportToast from './AutoImportToast';
import { SettingsSearch } from './settings/SettingsSearch';
import { useAuth } from '../contexts/AuthContext';
import { useUserAvatarUrl } from '../hooks/useUserAvatars';
import type { UserProfile, User } from '../services/auth.service';

interface LayoutProps {
  onLogout: () => void;
}

/**
 * Compose the best-available display name for the header dropdown. Priority:
 *   1. "FirstName LastName" from the User profile (admin-create populates
 *      these via the handle_new_user trigger + manage-users flow)
 *   2. Just first or just last if only one is set
 *   3. Email's local part as a last resort so we never render nothing
 */
function resolveDisplayName(user: User | UserProfile | null): string {
  if (!user) return '';
  const first = user.firstName?.trim() ?? '';
  const last = user.lastName?.trim() ?? '';
  const composed = [first, last].filter(Boolean).join(' ');
  if (composed) return composed;
  if (user.email) return user.email.split('@')[0];
  return '';
}

function Layout({ onLogout }: LayoutProps) {
  const { user } = useAuth();
  const userName = resolveDisplayName(user);
  const userEmail = user?.email ?? '';
  // M72: the photo an admin set on Manage Users; initials when there is none.
  const avatarUrl = useUserAvatarUrl(user?.id) ?? undefined;

  return (
    <div className="min-h-screen theme-bg-secondary">
      <Header
        isAuthenticated={true}
        onLogout={onLogout}
        userName={userName}
        userEmail={userEmail}
        avatarUrl={avatarUrl}
      />

      <div className="flex">
        <Sidebar />

        {/* `min-w-0` overrides the default `min-width: auto` on flex
            children — without it, any page with intrinsically-wide
            content (tables with whitespace-nowrap cells, long strings
            with no break points, etc.) stretches the flex container
            past the viewport and overlaps with the fixed sidebar on
            narrow windows.
            DO NOT add `overflow-x-hidden` here: per the CSS spec,
            setting overflow-x to a non-`visible` value silently
            promotes overflow-y to `auto`, which creates a new
            scroll-container context that breaks `position: sticky`
            for any descendant (e.g. the Properties detail side panel).
            Trust the per-page `min-w-0` discipline + per-table
            `overflow-x-auto` wrappers to contain wide content. */}
        <main className="flex-1 min-w-0 p-4">
          <Outlet />
        </main>
      </div>

      {/* Global Skip-Review progress toast. Pinned bottom-right;
          renders null when no auto-import is active so it doesn't
          take up space on routes that aren't using it. Mounted at
          Layout level so progress survives route changes — the
          earlier per-page banner disappeared the moment the user
          navigated to /properties to spot-check a record. */}
      <AutoImportToast />

      {/* Settings search: ⌘K / Ctrl-K on every page, and the Settings hub's search box (M286). */}
      <SettingsSearch />
    </div>
  );
}

export default Layout;